import React, { useEffect, useRef, useState } from 'react';
import VerificationLandingPage from './pages/verificationLandingPage/VerificationLandingPage';
import VerificationSuccessPage from './pages/verificationSuccessPage/VerificationSuccessPage';
import { TvsState, useTvsState } from './utils/useTvsState';
import VeriffModalPage from './pages/veriffModalPage/VeriffModalPage';
import { useVeriffSession } from './utils/veriffSessionContext';
import { VerificationStatus } from './@types/VerificationStatus';
import VeriffStatusCheckPage from './pages/veriffStatusCheckPage/VeriffStatusCheckPage';
import { trackEvent } from './tracking/tracking';
import { useClientConfig } from './utils/clientConfigContext';
import WebIdTanPage from './pages/webIdTanPage/WebIdTanPage';
import WebIdVerificationStartPage from './pages/webIdVerificationStartPage/WebIdVerificationStartPage';
import WebIdStatusCheckPage from './pages/webidStatusCheckPage/WebIdStatusCheckPage';
import WebIdDesktopHandoffPage from './pages/webIdDesktopHandoffPage/WebIdDesktopHandoffPage';

const getModalPage = (
  state: TvsState,
  sessionUrl: string | null,
  webIdPhoneNumber: string | null,
  setWebIdPhoneNumber: (phoneNumber: string | null) => void,
  clientId: string,
  prevStateRef: React.MutableRefObject<TvsState | null>,
  onVerificationComplete?: (status: VerificationStatus) => void,
  webIdDesktopHandoff?: { redirectUrl: string; sessionId: string; sourceSessionId: string } | null,
  setWebIdDesktopHandoff?: (
    handoff: { redirectUrl: string; sessionId: string; sourceSessionId: string } | null,
  ) => void,
  webIdSessionId?: string,
) => {
  const isNewState = state !== prevStateRef.current;
  prevStateRef.current = state;

  switch (state) {
    case 'verification_success':
      return <VerificationSuccessPage />;
    case 'verification_landing_page':
      return (
        <VerificationLandingPage
          shouldShowErrorMessage={false}
          setWebIdPhoneNumber={setWebIdPhoneNumber}
          onVerificationComplete={onVerificationComplete}
        />
      );
    case 'veriff_verification':
      return sessionUrl ? <VeriffModalPage sessionUrl={sessionUrl} /> : null;
    case 'verification_check_veriff':
      return <VeriffStatusCheckPage onVerificationComplete={onVerificationComplete} />;
    case 'verification_webid_tan':
      return (
        <WebIdTanPage
          phoneNumber={webIdPhoneNumber}
          clearPhoneNumber={() => setWebIdPhoneNumber(null)}
          onVerificationComplete={onVerificationComplete}
        />
      );
    case 'webid_verification_start':
      return webIdDesktopHandoff && webIdDesktopHandoff.sourceSessionId === webIdSessionId ? (
        <WebIdDesktopHandoffPage
          redirectUrl={webIdDesktopHandoff.redirectUrl}
          sessionId={webIdDesktopHandoff.sessionId}
          onVerificationComplete={onVerificationComplete}
        />
      ) : (
        <WebIdVerificationStartPage onDesktopHandoff={setWebIdDesktopHandoff} />
      );
    case 'webid_verification_check_page':
      return <WebIdStatusCheckPage onVerificationComplete={onVerificationComplete} />;
    case 'verification_failure_veriff':
      // Only track the event if this is a new state transition
      if (isNewState) {
        trackEvent(
          'is24.de.sicherheit.verifizierung.veriff',
          'verification_failed',
          'user',
          clientId,
          'veriff',
        ).catch();
      }
      onVerificationComplete?.(VerificationStatus.FAILED);
      return <VerificationLandingPage shouldShowErrorMessage={true} setWebIdPhoneNumber={setWebIdPhoneNumber} />;
    case 'verification_failure_webid':
      if (isNewState) {
        trackEvent('is24.de.sicherheit.verifizierung.webid', 'verification_failed', 'user', clientId, 'webid').catch();
      }
      onVerificationComplete?.(VerificationStatus.FAILED);
      return <VerificationLandingPage shouldShowErrorMessage={true} setWebIdPhoneNumber={setWebIdPhoneNumber} />;
    default:
      return null;
  }
};

interface RootProps {
  onVerificationComplete?: (status: VerificationStatus) => void;
}

const Root: React.FC<RootProps> = ({ onVerificationComplete }) => {
  const [tvsState] = useTvsState();
  const { sessionUrl } = useVeriffSession();
  const { clientId } = useClientConfig();
  const prevStateRef = useRef<TvsState | null>(null);
  const [webIdPhoneNumber, setWebIdPhoneNumber] = useState<string | null>(null);
  const webIdSessionId = new URLSearchParams(window.location.search).get('webid_session_id') || '';
  const [webIdDesktopHandoff, setWebIdDesktopHandoff] = useState<{
    redirectUrl: string;
    sessionId: string;
    sourceSessionId: string;
  } | null>(null);

  useEffect(() => {
    if (tvsState !== 'webid_verification_start' || webIdDesktopHandoff?.sourceSessionId !== webIdSessionId) {
      setWebIdDesktopHandoff(null);
    }
  }, [tvsState, webIdDesktopHandoff, webIdSessionId]);

  if (!tvsState) return null;

  return (
    <div className='modal'>
      {getModalPage(
        tvsState,
        sessionUrl,
        webIdPhoneNumber,
        setWebIdPhoneNumber,
        clientId,
        prevStateRef,
        onVerificationComplete,
        webIdDesktopHandoff,
        setWebIdDesktopHandoff,
        webIdSessionId,
      )}
    </div>
  );
};

export default Root;
